import { useCallback, useEffect, useState } from 'react';
import { ProductListMode } from 'corgi-types';

import { useIsObserved } from 'common/hooks/intersection-observer';
import { getUrlWithQueryParams, updateUrl, updateUrlAndEmitEvent } from 'common/utils/url';
import type { UseProductListProps } from '../components/types';
import { useArticles } from './use-articles';
import { useFetchDeferredProducts } from './use-fetch-deferred';
import { useFetchProducts } from './use-fetch-products';
import { useLoadMoreOrPrevious } from './use-load-more-or-previous';

// eslint-disable-next-line max-lines-per-function
export const useProductsList = ({
  id,
  query,
  articles: initialArticles,
  totalCount: initialTotalCount,
  page: initialPage = 0,
  mode,
  initialPageSize,
  pageSize,
  sortingScore,
  params,
  withCampaignBadge,
  deferInitialLoad = false,
}: UseProductListProps) => {
  const { prependArticles, appendArticles, articles } = useArticles(initialArticles);
  const [totalCount, setTotalCount] = useState(initialTotalCount);
  const [page, setPage] = useState(initialPage);
  const [minLoadedPage, setMinLoadedPage] = useState(initialPage);
  const [maxLoadedPage, setMaxLoadedPage] = useState(initialPage);
  const offset = page * pageSize + pageSize;
  const isSearchMode = mode === ProductListMode.Search;
  const { fetchProducts, isLoading } = useFetchProducts({
    pageSize,
    isSearchMode,
    query,
    id,
    sortingScore,
    params,
    setTotalCount,
    withCampaignBadge,
  });

  const [skeletonRef, observerShouldFetch] = useIsObserved();
  const [deferredFetchUnlocked, setDeferredFetchUnlocked] = useState(!deferInitialLoad);
  // When deferInitialLoad is true, the skeleton doesn't render (effectiveArticlesToFetch = 0),
  // so observerShouldFetch never becomes true. Once manually unlocked, fetch immediately.
  const shouldFetch = deferInitialLoad ? deferredFetchUnlocked : observerShouldFetch;

  const triggerDeferredFetch = useCallback(() => {
    setDeferredFetchUnlocked(true);
  }, []);

  const { hasFetchedDeferred, articlesToFetch } = useFetchDeferredProducts({
    fetchProducts,
    appendArticles,
    offset,
    initialPageSize,
    pageSize,
    page,
    totalCount,
    shouldFetch,
  });

  // Hide skeleton placeholders while deferred fetch is blocked
  const effectiveArticlesToFetch = deferredFetchUnlocked ? articlesToFetch : 0;

  const onCurrentDisplayPageChange = (newPage: number) => {
    if (newPage) {
      updateUrlAndEmitEvent(getUrlWithQueryParams({ page: newPage + 1 }));
    } else {
      // Remove page parameter when on first page (index 0 is the default)
      updateUrlAndEmitEvent(getUrlWithQueryParams({ page: '' }));
    }
  };

  const {
    fetchPrevProducts,
    fetchMoreProducts,
    fetchingArticlesCount,
    isFetchingPrev,
    prevPageOffset,
    currentDisplayPage,
  } = useLoadMoreOrPrevious({
    articles,
    appendArticles,
    prependArticles,
    offset,
    fetchProducts,
    articlesToFetch: effectiveArticlesToFetch,
    page,
    setPage,
    minLoadedPage,
    maxLoadedPage,
    setMinLoadedPage,
    setMaxLoadedPage,
    pageSize,
    onCurrentDisplayPageChange,
  });

  useEffect(() => {
    // remove deprecated step param
    updateUrl(getUrlWithQueryParams({ step: '' }));
  }, []);

  return {
    totalCount,
    articles,
    prevPageOffset,
    offset,
    isLoading,
    hasFetchedDeferred,
    triggerDeferredFetch,
    fetchingArticlesCount,
    isFetchingPrev,
    isSearchMode,
    fetchPrevProducts,
    fetchMoreProducts,
    page: currentDisplayPage + 1,
    skeletonRef,
  };
};
